/**
 * Cubitts Chat Widget - Web Component
 * A customer-facing chat widget that connects to the ai-chat-api via shopify-app-proxy
 *
 * Configuration:
 * - data-api-url: API base URL (default: /apps/chat for App Proxy auth)
 *   - Use "/apps/chat" for storefront (Shopify signs requests with customer ID)
 *   - Use "https://app-proxy.cubitts.com" for direct access (no auth)
 *
 * Sends to POST {apiUrl}/api/chat:
 * - message: string
 * - conversationId: string (optional, for continuing conversations)
 * - currentUrl: string (full URL of the current page)
 * - pageType: string (home, product, collection, cart, account, search, page, checkout, other)
 */

class CubittsChat extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.isOpen = false;
    this.messages = [];
    this.conversationId = null;
    this.isLoading = false;
  }

  connectedCallback() {
    // Default to App Proxy URL for authenticated storefront requests
    // Shopify intercepts /apps/chat/* and forwards with signed customer ID
    this.apiUrl = this.dataset.apiUrl || '/apps/chat';
    this.isLoggedIn = this.dataset.customerLoggedIn === 'true';
    this.render();
    this.loadHistory();
  }

  get styles() {
    return `
      :host {
        --chat-primary: #1a1a1a;
        --chat-primary-hover: #333;
        --chat-bg: #ffffff;
        --chat-border: #e5e5e5;
        --chat-text: #1a1a1a;
        --chat-text-muted: #666;
        --chat-user-bg: #1a1a1a;
        --chat-user-text: #ffffff;
        --chat-assistant-bg: #f5f5f5;
        --chat-radius: 12px;
        --chat-shadow: 0 4px 24px rgba(0,0,0,0.15);
        font-family: "Suisse Int'l", -apple-system, BlinkMacSystemFont, sans-serif;
      }

      * {
        box-sizing: border-box;
        margin: 0;
        padding: 0;
      }

      .chat-container {
        position: fixed;
        bottom: 20px;
        right: 20px;
        z-index: 9999;
        font-size: 14px;
        line-height: 1.5;
      }

      .chat-bubble {
        width: 56px;
        height: 56px;
        border-radius: 50%;
        background: var(--chat-primary);
        color: #fff;
        border: none;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: var(--chat-shadow);
        transition: transform 0.2s, background 0.2s;
      }

      .chat-bubble:hover {
        background: var(--chat-primary-hover);
        transform: scale(1.05);
      }

      .chat-bubble svg {
        width: 24px;
        height: 24px;
      }

      .chat-window {
        display: none;
        position: absolute;
        bottom: 70px;
        right: 0;
        width: 380px;
        max-width: calc(100vw - 40px);
        height: 500px;
        max-height: calc(100vh - 120px);
        background: var(--chat-bg);
        border-radius: var(--chat-radius);
        box-shadow: var(--chat-shadow);
        flex-direction: column;
        overflow: hidden;
      }

      .chat-window.open {
        display: flex;
      }

      .chat-header {
        padding: 16px;
        background: var(--chat-primary);
        color: #fff;
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      .chat-header h3 {
        font-size: 15px;
        font-weight: 500;
        letter-spacing: 0.02em;
      }

      .chat-close {
        background: none;
        border: none;
        color: #fff;
        cursor: pointer;
        padding: 4px;
        opacity: 0.8;
        transition: opacity 0.2s;
      }

      .chat-close:hover {
        opacity: 1;
      }

      .chat-close svg {
        width: 20px;
        height: 20px;
      }

      .chat-messages {
        flex: 1;
        overflow-y: auto;
        padding: 16px;
        display: flex;
        flex-direction: column;
        gap: 12px;
      }

      .message {
        max-width: 85%;
        padding: 10px 14px;
        border-radius: 16px;
        word-wrap: break-word;
      }

      .message.user {
        align-self: flex-end;
        background: var(--chat-user-bg);
        color: var(--chat-user-text);
        border-bottom-right-radius: 4px;
      }

      .message.assistant {
        align-self: flex-start;
        background: var(--chat-assistant-bg);
        color: var(--chat-text);
        border-bottom-left-radius: 4px;
      }

      .message.welcome {
        text-align: center;
        max-width: 100%;
        background: none;
        color: var(--chat-text-muted);
        font-size: 13px;
        padding: 20px;
      }

      .chat-input-container {
        padding: 12px 16px;
        border-top: 1px solid var(--chat-border);
        display: flex;
        gap: 8px;
      }

      .chat-input {
        flex: 1;
        padding: 10px 14px;
        border: 1px solid var(--chat-border);
        border-radius: 20px;
        font-size: 14px;
        font-family: inherit;
        outline: none;
        transition: border-color 0.2s;
      }

      .chat-input:focus {
        border-color: var(--chat-primary);
      }

      .chat-input:disabled {
        background: #f9f9f9;
        cursor: not-allowed;
      }

      .chat-send {
        width: 40px;
        height: 40px;
        border-radius: 50%;
        background: var(--chat-primary);
        color: #fff;
        border: none;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: background 0.2s, opacity 0.2s;
      }

      .chat-send:hover:not(:disabled) {
        background: var(--chat-primary-hover);
      }

      .chat-send:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .chat-send svg {
        width: 18px;
        height: 18px;
      }

      .typing-indicator {
        display: flex;
        gap: 4px;
        padding: 10px 14px;
        background: var(--chat-assistant-bg);
        border-radius: 16px;
        border-bottom-left-radius: 4px;
        align-self: flex-start;
      }

      .typing-indicator span {
        width: 8px;
        height: 8px;
        background: var(--chat-text-muted);
        border-radius: 50%;
        animation: typing 1.4s infinite;
      }

      .typing-indicator span:nth-child(2) {
        animation-delay: 0.2s;
      }

      .typing-indicator span:nth-child(3) {
        animation-delay: 0.4s;
      }

      @keyframes typing {
        0%, 60%, 100% {
          transform: translateY(0);
          opacity: 0.4;
        }
        30% {
          transform: translateY(-4px);
          opacity: 1;
        }
      }

      .error-message {
        background: #fef2f2;
        color: #991b1b;
        padding: 8px 12px;
        border-radius: 8px;
        font-size: 13px;
        margin: 8px 16px;
      }

      @media (max-width: 480px) {
        .chat-window {
          width: 100%;
          height: 100%;
          max-height: 100vh;
          bottom: 0;
          right: 0;
          border-radius: 0;
        }

        .chat-container {
          bottom: 16px;
          right: 16px;
        }
      }
    `;
  }

  render() {
    this.shadowRoot.innerHTML = `
      <style>${this.styles}</style>
      <div class="chat-container">
        <button class="chat-bubble" aria-label="Open chat">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
        </button>
        <div class="chat-window">
          <div class="chat-header">
            <h3>Cubitts</h3>
            <button class="chat-close" aria-label="Close chat">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>
          </div>
          <div class="chat-messages">
            <div class="message welcome">
              Hello! How can we help you today?
            </div>
          </div>
          <div class="chat-input-container">
            <input type="text" class="chat-input" placeholder="Type your message..." />
            <button class="chat-send" aria-label="Send message">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    `;

    this.bindEvents();
  }

  bindEvents() {
    const bubble = this.shadowRoot.querySelector('.chat-bubble');
    const closeBtn = this.shadowRoot.querySelector('.chat-close');
    const input = this.shadowRoot.querySelector('.chat-input');
    const sendBtn = this.shadowRoot.querySelector('.chat-send');

    bubble.addEventListener('click', () => this.toggle());
    closeBtn.addEventListener('click', () => this.close());

    input.addEventListener('keypress', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.sendMessage();
      }
    });

    sendBtn.addEventListener('click', () => this.sendMessage());
  }

  toggle() {
    this.isOpen = !this.isOpen;
    const window = this.shadowRoot.querySelector('.chat-window');
    window.classList.toggle('open', this.isOpen);

    if (this.isOpen) {
      this.shadowRoot.querySelector('.chat-input').focus();
    }
  }

  open() {
    if (!this.isOpen) {
      this.isOpen = true;
      const window = this.shadowRoot.querySelector('.chat-window');
      window.classList.add('open');
      this.shadowRoot.querySelector('.chat-input').focus();
    }
  }

  close() {
    this.isOpen = false;
    this.shadowRoot.querySelector('.chat-window').classList.remove('open');
  }

  /**
   * Open chat and pre-fill input with a message
   * @param {string} message - Message to pre-fill in the input
   * @param {boolean} autoSend - If true, automatically send the message (default: true)
   */
  openWithMessage(message, autoSend = true) {
    this.open();
    const input = this.shadowRoot.querySelector('.chat-input');
    input.value = message;
    input.focus();

    if (autoSend) {
      // Small delay to ensure UI is ready
      setTimeout(() => this.sendMessage(), 100);
    }
  }

  async sendMessage() {
    const input = this.shadowRoot.querySelector('.chat-input');
    const message = input.value.trim();

    if (!message || this.isLoading) return;

    input.value = '';
    this.addMessage('user', message);
    this.showTypingIndicator();
    this.isLoading = true;
    this.updateInputState();

    try {
      const response = await this.callChatApi(message);
      this.hideTypingIndicator();

      if (response.success) {
        this.conversationId = response.data.conversationId;
        this.addMessage('assistant', response.data.message);
        this.saveHistory();
      } else {
        this.showError(response.error || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('[CubittsChat] Error:', error);
      this.hideTypingIndicator();
      this.showError('Unable to connect. Please check your connection and try again.');
    } finally {
      this.isLoading = false;
      this.updateInputState();
    }
  }

  async callChatApi(message) {
    // When using App Proxy (/apps/chat/*), Shopify automatically adds
    // signed query params including logged_in_customer_id for auth
    const url = `${this.apiUrl}/api/chat`;

    // Build history from previous messages (exclude current message, limit to last 10)
    const history = this.messages.slice(-10).map((msg) => ({
      role: msg.role,
      content: msg.content,
    }));

    const body = {
      message,
      conversationId: this.conversationId,
      currentUrl: window.location.href,
      pageType: this.getPageType(),
      history,
    };

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    return response.json();
  }

  /**
   * Determine the page type from the current URL path
   * @returns {string} Page type: product, collection, cart, account, search, page, or home
   */
  getPageType() {
    const path = window.location.pathname.toLowerCase();

    if (path === '/' || path === '') {
      return 'home';
    }
    if (path.startsWith('/products/')) {
      return 'product';
    }
    if (path.startsWith('/collections/')) {
      return 'collection';
    }
    if (path === '/cart') {
      return 'cart';
    }
    if (path.startsWith('/account')) {
      return 'account';
    }
    if (path === '/search' || path.startsWith('/search')) {
      return 'search';
    }
    if (path.startsWith('/pages/')) {
      return 'page';
    }
    if (path === '/checkout' || path.startsWith('/checkout')) {
      return 'checkout';
    }

    return 'other';
  }

  addMessage(role, content) {
    this.messages.push({ role, content, timestamp: Date.now() });

    const messagesContainer = this.shadowRoot.querySelector('.chat-messages');
    const messageEl = document.createElement('div');
    messageEl.className = `message ${role}`;
    messageEl.textContent = content;
    messagesContainer.appendChild(messageEl);
    messagesContainer.scrollTop = messagesContainer.scrollHeight;
  }

  showTypingIndicator() {
    const messagesContainer = this.shadowRoot.querySelector('.chat-messages');
    const indicator = document.createElement('div');
    indicator.className = 'typing-indicator';
    indicator.innerHTML = '<span></span><span></span><span></span>';
    messagesContainer.appendChild(indicator);
    messagesContainer.scrollTop = messagesContainer.scrollHeight;
  }

  hideTypingIndicator() {
    const indicator = this.shadowRoot.querySelector('.typing-indicator');
    if (indicator) {
      indicator.remove();
    }
  }

  showError(message) {
    const messagesContainer = this.shadowRoot.querySelector('.chat-messages');
    const errorEl = document.createElement('div');
    errorEl.className = 'error-message';
    errorEl.textContent = message;
    messagesContainer.appendChild(errorEl);
    messagesContainer.scrollTop = messagesContainer.scrollHeight;

    // Auto-remove error after 5 seconds
    setTimeout(() => {
      errorEl.remove();
    }, 5000);
  }

  updateInputState() {
    const input = this.shadowRoot.querySelector('.chat-input');
    const sendBtn = this.shadowRoot.querySelector('.chat-send');

    input.disabled = this.isLoading;
    sendBtn.disabled = this.isLoading;
  }

  saveHistory() {
    try {
      const data = {
        conversationId: this.conversationId,
        messages: this.messages.slice(-20), // Keep last 20 messages
        timestamp: Date.now(),
      };
      localStorage.setItem('cubitts_chat_history', JSON.stringify(data));
    } catch (error) {
      console.warn('[CubittsChat] Could not save history:', error);
    }
  }

  loadHistory() {
    try {
      const saved = localStorage.getItem('cubitts_chat_history');
      if (!saved) return;

      const data = JSON.parse(saved);

      // Only restore if less than 1 hour old
      if (Date.now() - data.timestamp > 60 * 60 * 1000) {
        localStorage.removeItem('cubitts_chat_history');
        return;
      }

      this.conversationId = data.conversationId;
      const messagesContainer = this.shadowRoot.querySelector('.chat-messages');

      // Clear welcome message if we have history
      if (data.messages.length > 0) {
        messagesContainer.innerHTML = '';
      }

      data.messages.forEach(msg => {
        this.messages.push(msg);
        const messageEl = document.createElement('div');
        messageEl.className = `message ${msg.role}`;
        messageEl.textContent = msg.content;
        messagesContainer.appendChild(messageEl);
      });

      messagesContainer.scrollTop = messagesContainer.scrollHeight;
    } catch (error) {
      console.warn('[CubittsChat] Could not load history:', error);
    }
  }
}

// Register the custom element
if (!customElements.get('cubitts-chat-widget')) {
  customElements.define('cubitts-chat-widget', CubittsChat);
}

/**
 * Global CubittsChat API
 * Allows external scripts to control the chat widget
 * Compatible with Gorgias-style integration patterns
 */
window.CubittsChat = {
  /**
   * Open the chat widget
   */
  open: function () {
    const widget = document.querySelector('cubitts-chat-widget');
    if (widget) {
      widget.open();
    }
  },

  /**
   * Close the chat widget
   */
  close: function () {
    const widget = document.querySelector('cubitts-chat-widget');
    if (widget) {
      widget.close();
    }
  },

  /**
   * Toggle the chat widget open/closed
   */
  toggle: function () {
    const widget = document.querySelector('cubitts-chat-widget');
    if (widget) {
      widget.toggle();
    }
  },

  /**
   * Send a message (opens chat and sends immediately)
   * @param {string} message - The message to send
   */
  sendMessage: function (message) {
    const widget = document.querySelector('cubitts-chat-widget');
    if (widget && message) {
      widget.openWithMessage(message, true);
    }
  },

  /**
   * Open chat with a pre-filled message (does not auto-send)
   * @param {string} message - The message to pre-fill
   */
  openWithMessage: function (message) {
    const widget = document.querySelector('cubitts-chat-widget');
    if (widget && message) {
      widget.openWithMessage(message, false);
    }
  },
};

/**
 * Event listener for trigger buttons throughout the theme
 * Supports both js-cubitts-chat-message (new) and js-gorgias-chat-message (legacy) attributes
 * Usage: <button js-cubitts-chat-message="Help me find the right frame size">Chat</button>
 */
document.addEventListener('click', function (e) {
  const target = e.target.closest('[js-cubitts-chat-message]') || e.target.closest('[js-gorgias-chat-message]');
  if (!target) return;

  const message = target.getAttribute('js-cubitts-chat-message') || target.getAttribute('js-gorgias-chat-message');
  if (message) {
    e.preventDefault();
    window.CubittsChat.sendMessage(message);
  }
});
